import React, { useEffect, useMemo, useState } from 'react';

import './ECGRhythmTimeline.scss';
import { Card, Col, Row, Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHeartPulse } from '@fortawesome/free-solid-svg-icons';
import { PatientVisit } from '../../amplify/api-gateway/search-patients';
import { getLocalDateTimeStr } from '../../utils/time';

const MIN_SEGMENT_DURATION = 60;

// Function to create ECGData from raw arrays
export const createECGDataFromArrays = (
    waveformAnnotationTimestamps: number[],
    waveformAnnotation: string[],
    hrTimestamps: number[],
    hr: (number | null)[]
): ECGData => {
    if (waveformAnnotationTimestamps.length !== waveformAnnotation.length) {
        throw new Error('Waveform annotation timestamps and annotations arrays must have the same length');
    }
    if (hrTimestamps.length !== hr.length) {
        throw new Error('HR timestamps and HR arrays must have the same length');
    }
    if (waveformAnnotationTimestamps.length === 0) {
        throw new Error('Waveform annotation arrays cannot be empty');
    }

    // Convert Unix timestamps to Date objects and sort by timestamp
    const rhythmData = waveformAnnotationTimestamps
        .map((timestamp, index) => ({
            timestamp,
            rhythm: waveformAnnotation[index],
            date: new Date(timestamp * 1000)
        }))
        .sort((a, b) => a.timestamp - b.timestamp);

    // Filter and sort HR data, removing null values
    const hrData = hrTimestamps
        .map((timestamp, index) => ({
            timestamp,
            hr: hr[index],
            date: new Date(timestamp * 1000)
        }))
        .filter(item => item.hr !== null && !isNaN(item.hr as number))
        .sort((a, b) => a.timestamp - b.timestamp) as Array<{
            timestamp: number;
            hr: number;
            date: Date;
        }>;

    // Create rhythm segments
    const segments: ECGSegment[] = [];
    
    for (let i = 0; i < rhythmData.length; i++) {
        const current = rhythmData[i];
        const next = rhythmData[i + 1];
        
        // Determine segment end time
        const segmentEnd = next ? next.date : new Date(Math.max(...waveformAnnotationTimestamps) * 1000);
        const segmentDuration = Math.floor((segmentEnd.getTime() - current.date.getTime()) / 1000);
        
        // Skip segments that are too short
        if (segmentDuration < MIN_SEGMENT_DURATION) {
            continue;
        }

        // Find HR data within this segment's time range
        const segmentHRData = hrData.filter(hrItem => 
            hrItem.timestamp >= current.timestamp && 
            hrItem.timestamp < (next ? next.timestamp : Math.max(...waveformAnnotationTimestamps))
        );

        // Calculate HR statistics for this segment
        let hr_mean: number | undefined;
        let hr_min: number | undefined;
        let hr_max: number | undefined;

        if (segmentHRData.length > 0) {
            const hrValues = segmentHRData.map(item => item.hr);
            hr_mean = hrValues.reduce((sum, val) => sum + val, 0) / hrValues.length;
            hr_min = Math.min(...hrValues);
            hr_max = Math.max(...hrValues);
        }

        segments.push({
            segment_start_pt: current.date.toISOString(),
            segment_end_pt: segmentEnd.toISOString(),
            segment_duration: segmentDuration,
            segment_rhythm: current.rhythm,
            hr_mean,
            hr_min,
            hr_max
        });
    }

    // Merge consecutive segments with the same rhythm
    const mergedSegments: ECGSegment[] = [];
    
    for (const segment of segments) {
        const lastSegment = mergedSegments[mergedSegments.length - 1];
        
        if (lastSegment && lastSegment.segment_rhythm === segment.segment_rhythm) {
            // Merge with previous segment
            const combinedHRValues: number[] = [];
            
            // Collect HR data from both segments
            const startTime = new Date(lastSegment.segment_start_pt).getTime() / 1000;
            const endTime = new Date(segment.segment_end_pt).getTime() / 1000;
            
            const combinedHRData = hrData.filter(hrItem => 
                hrItem.timestamp >= startTime && hrItem.timestamp < endTime
            );
            
            if (combinedHRData.length > 0) {
                const hrValues = combinedHRData.map(item => item.hr);
                lastSegment.hr_mean = hrValues.reduce((sum, val) => sum + val, 0) / hrValues.length;
                lastSegment.hr_min = Math.min(...hrValues);
                lastSegment.hr_max = Math.max(...hrValues);
            }
            
            lastSegment.segment_end_pt = segment.segment_end_pt;
            lastSegment.segment_duration = Math.floor(
                (new Date(segment.segment_end_pt).getTime() - new Date(lastSegment.segment_start_pt).getTime()) / 1000
            );
        } else {
            mergedSegments.push({ ...segment });
        }
    }

    // Calculate statistics
    const totalDuration = mergedSegments.reduce((sum, segment) => sum + segment.segment_duration, 0);
    const rhythmBurdens: Record<string, number> = {};
    const rhythmEpisodeCounts: Record<string, number> = {};
    const rhythmDurations: Record<string, { max_episode_seconds: number }> = {};

    // Group segments by rhythm
    const rhythmGroups: Record<string, ECGSegment[]> = {};
    mergedSegments.forEach(segment => {
        if (!rhythmGroups[segment.segment_rhythm]) {
            rhythmGroups[segment.segment_rhythm] = [];
        }
        rhythmGroups[segment.segment_rhythm].push(segment);
    });

    // Calculate statistics for each rhythm
    Object.entries(rhythmGroups).forEach(([rhythm, segments]) => {
        const totalRhythmDuration = segments.reduce((sum, segment) => sum + segment.segment_duration, 0);
        rhythmBurdens[rhythm] = totalDuration > 0 ? totalRhythmDuration / totalDuration : 0;
        rhythmEpisodeCounts[rhythm] = segments.length;
        
        const maxEpisodeDuration = Math.max(...segments.map(segment => segment.segment_duration));
        rhythmDurations[rhythm] = { max_episode_seconds: maxEpisodeDuration };
    });

    return {
        ecg_continuous_granular: mergedSegments,
        ecg_continuous_stats: {
            rhythm_burdens: rhythmBurdens,
            rhythm_episode_counts: rhythmEpisodeCounts,
            rhythm_durations: rhythmDurations
        }
    };
};

export interface ECGSegment {
    segment_start_pt: string;
    segment_end_pt: string;
    segment_duration: number;
    segment_rhythm: string;
    hr_mean?: number;
    hr_min?: number;
    hr_max?: number;
}

export interface ECGStats {
    rhythm_burdens?: Record<string, number>;
    rhythm_episode_counts?: Record<string, number>;
    rhythm_durations?: Record<string, { max_episode_seconds: number }>;
}

export interface ECGData {
    ecg_continuous_granular: ECGSegment[];
    ecg_continuous_stats?: ECGStats;
}

export interface TimeRange {
    minTime: Date;
    maxTime: Date;
    totalDuration: number;
}

export interface HighlightRange {
    start: number;
    end: number;
}

export interface ECGRhythmTimelineProps {
    waveformAvailabilityData: any,
    numericsData: any,
    setHighlightRange: (range: any) => void;
    vsTimeRange?: TimeRange;
    addAnnotationToPlots: any;
    patientVisit: PatientVisit;
}


// Parse PT time string to JavaScript Date
const parsePTTime = (timeStr: string) => {
    if (!timeStr) return null;
    try {
        // Remove ' PT' suffix and parse
        const cleanStr = timeStr.replace(' PT', '');
        return new Date(cleanStr);
    } catch (e) {
        console.warn('Could not parse time:', timeStr);
        return null;
    }
};

// Get rhythm color class
const getRhythmColor = (rhythm: any) => {
    if (!rhythm) return '#6b7280'; // gray
    const r = rhythm.toLowerCase();
    
    // Sinus rhythms
    if (r.includes('sinus rhythm') || (r.includes('sinus') && !r.includes('tachycardia') && !r.includes('bradycardia'))) return '#10b981'; // green
    if (r.includes('sinus tachycardia')) return '#3b82f6'; // blue
    if (r.includes('sinus bradycardia')) return '#06b6d4'; // cyan
    
    // Atrial rhythms
    if (r.includes('atrial fibrillation') || r.includes('afib rhythm') || r.includes('af')) return '#ef4444'; // red
    if (r.includes('atrial flutter')) return '#dc2626'; // darker red
    if (r.includes('sv tach') || r.includes('supraventricular tachycardia') || r.includes('svt')) return '#f59e0b'; // amber
    if (r.includes('sv rhythm') || (r.includes('supraventricular') && !r.includes('tachycardia'))) return '#8b5cf6'; // purple
    
    // Irregular rhythms
    if (r.includes('irregular hr') || r.includes('irregular heart rate')) return '#ec4899'; // pink
    if (r.includes('multifocal atrial tachycardia') || r.includes('mat')) return '#db2777'; // darker pink
    
    // Ventricular rhythms
    if (r.includes('ventricular tachycardia') || r.includes('vt')) return '#7c2d12'; // brown
    if (r.includes('ventricular fibrillation') || r.includes('vf')) return '#991b1b'; // dark red
    
    // Other specific rhythms
    if (r.includes('junctional')) return '#059669'; // emerald
    if (r.includes('paced') || r.includes('pacing')) return '#7c3aed'; // violet
    if (r.includes('unknown')) return '#6b7280'; // gray
    
    return '#8b5cf6'; // purple for other
};

// Get rhythm display name
const getRhythmDisplayName = (rhythm: any) => {
    if (!rhythm) return 'Unknown';
    const r = rhythm.toLowerCase();
    
    // Sinus rhythms
    if (r.includes('sinus rhythm') || (r.includes('sinus') && !r.includes('tachycardia') && !r.includes('bradycardia'))) return 'Sinus Rhythm';
    if (r.includes('sinus tachycardia') || r.includes('sinus tach')) return 'Sinus Tachycardia';
    if (r.includes('sinus bradycardia')) return 'Sinus Bradycardia';
    
    // Atrial rhythms
    if (r.includes('atrial fibrillation') || r.includes('afib rhythm') || r.includes('af')) return 'Atrial Fibrillation';
    if (r.includes('atrial flutter')) return 'Atrial Flutter';
    
    // Supraventricular rhythms
    if (r.includes('sv tach') || r.includes('supraventricular tachycardia') || r.includes('svt')) return 'SV Tach';
    if (r.includes('sv rhythm') || (r.includes('supraventricular') && !r.includes('tachycardia'))) return 'SV Rhythm';
    
    // Irregular rhythms
    if (r.includes('irregular hr') || r.includes('irregular heart rate')) return 'Irregular HR';
    if (r.includes('multifocal atrial tachycardia') || r.includes('mat')) return 'MAT';
    
    // Ventricular rhythms
    if (r.includes('ventricular tachycardia') || r.includes('vt')) return 'V-Tach';
    if (r.includes('ventricular fibrillation') || r.includes('vf')) return 'V-Fib';
    
    // Other specific rhythms
    if (r.includes('junctional')) return 'Junctional';
    if (r.includes('paced') || r.includes('pacing')) return 'Paced';
    if (r.includes('unknown')) return 'Unknown';
    if (r.includes('invalid')) return 'Unknown';
    
    return rhythm; // Return original if no match
};

const ECGRhythmTimeline: React.FC<ECGRhythmTimelineProps> = ({
    waveformAvailabilityData,
    numericsData,
    setHighlightRange,
    vsTimeRange,
    addAnnotationToPlots,
    patientVisit
}) => {
    const [selectedSegment, setSelectedSegment] = useState<ECGSegment | null>(null);
    const [highlightedRhythm, setHighlightedRhythm] = useState<string | null>(null);

    const data = useMemo(() => {
        if (waveformAvailabilityData && waveformAvailabilityData.length > 8 && numericsData && numericsData["HR"]) {
            const hrTimestamps = numericsData["HR"][0];
            const hr = numericsData["HR"][1];
            const waveformTimestamps = waveformAvailabilityData[0];
            const waveformAnnotations = waveformAvailabilityData[8];
            return createECGDataFromArrays(waveformTimestamps, waveformAnnotations, hrTimestamps, hr);
        }
        return null;
    }, [waveformAvailabilityData, numericsData]);

    const ecgGranular = data?.ecg_continuous_granular || [];
    const filteredSegments = ecgGranular.filter((segment) => segment.segment_duration >= MIN_SEGMENT_DURATION);

    if (filteredSegments.length === 0) {
        return <></>;
    }

    // if (filteredSegments.length === 0) {
    //     return (
    //         <p className="text-gray-500 italic">
    //             No ECG rhythm segments found with duration ≥ {MIN_SEGMENT_DURATION} seconds.
    //         </p>
    //     );
    // }

    let minTime: any, maxTime: any, totalDuration: any;

    if (vsTimeRange) {
        ({ minTime, maxTime, totalDuration } = vsTimeRange);
        minTime = minTime.getTime();
        maxTime = maxTime.getTime();
    } else {
        const startTimes = filteredSegments.map((s: any) => parsePTTime(s.segment_start_pt)).filter((val) => val !== null).map((d) => d ? d.getTime() : 0);
        const endTimes = filteredSegments.map((s: any) => parsePTTime(s.segment_end_pt)).filter((val) => val !== null).map((d) => d ? d.getTime() : 0);

        if (startTimes.length === 0 || endTimes.length === 0) {
            return <p className="text-gray-500 italic">Could not parse ECG segment times.</p>;
        }

        minTime = Math.min(...startTimes);
        maxTime = Math.max(...endTimes);
        totalDuration = maxTime - minTime;
    }

    const uniqueRhythms = [...new Set(filteredSegments.map((s: any) => getRhythmDisplayName(s.segment_rhythm)))];

    const handleSegmentClick = (segment: any, startTime: Date, endTime: Date) => {
        setSelectedSegment(segment);
        setHighlightRange({ start: startTime.getTime(), end: endTime.getTime() });
        const lengthInSeconds = (endTime.getTime() - startTime.getTime()) / 1000;
        addAnnotationToPlots(startTime.getTime() / 1000, patientVisit, "eventMark", `${segment.segment_rhythm}`, lengthInSeconds);
    };

    const timelineSegments = filteredSegments.map((segment: any, index: number) => {
        const startTime = parsePTTime(segment.segment_start_pt);
        const endTime = parsePTTime(segment.segment_end_pt);
        if (!startTime || !endTime || !minTime) return null;

        const leftPercent = ((startTime.getTime() - minTime) / totalDuration) * 100;
        const widthPercent = ((endTime.getTime() - startTime.getTime()) / totalDuration) * 100;
        const color = getRhythmColor(segment.segment_rhythm);
        const rhythmDisplayName = getRhythmDisplayName(segment.segment_rhythm);
        const isHighlighted = highlightedRhythm === rhythmDisplayName;

        return (
            <div
                key={index}
                className="ecg-timeline-segment"
                style={{
                    left: `${leftPercent}%`,
                    width: `${widthPercent}%`,
                    backgroundColor: color,
                    opacity: highlightedRhythm && !isHighlighted ? 0.3 : 1,
                    zIndex: isHighlighted ? 10 : 1,
                    position: 'absolute',
                    height: '100%',
                    borderRight: '1px solid rgba(0,0,0,0.1)',
                    cursor: 'pointer',
                    transition: 'opacity 0.2s ease, z-index 0.2s ease',
                }}
                onClick={() => handleSegmentClick(segment, startTime, endTime)}
                title={`${segment.segment_rhythm} - ${segment.segment_duration}s`}
            />
        );
    });

    const legendItems = uniqueRhythms.map((name, index) => (
        <div className="ecg-legend-item" key={index}>
            <div className="ecg-legend-color" style={{ backgroundColor: getRhythmColor(name) }} />
            <span className="text-sm text-gray-700">{name}</span>
        </div>
    ));

    // Rhythm Stats
    const { rhythm_burdens = {}, rhythm_episode_counts = {}, rhythm_durations = {} } = data?.ecg_continuous_stats || {};

    const getOriginalRhythmName = (displayName: string, segments: any) => {
        return segments[0]?.segment_rhythm ||
            filteredSegments.find((s) => getRhythmDisplayName(s.segment_rhythm) === displayName)?.segment_rhythm ||
            displayName;
    };

    const rhythmStats = uniqueRhythms
        .map((rhythmName) => {
            const segments = filteredSegments.filter(
                (s) => getRhythmDisplayName(s.segment_rhythm) === rhythmName
            );
            if (!segments.length) return null;

            const originalName = getOriginalRhythmName(rhythmName, segments);
            const burdenPercent = (rhythm_burdens[originalName] || 0) * 100;
            const totalSegments = rhythm_episode_counts[originalName] || 0;
            const longestSegment = segments.reduce((a, b) => (b.segment_duration > a.segment_duration ? b : a), segments[0]);
            const longestEpisodeSeconds = rhythm_durations[originalName]?.max_episode_seconds || 0;
            const longestEpisodeMinutes = (longestEpisodeSeconds / 60).toFixed(1);
            const longestEpisodeStartTime = longestSegment.segment_start_pt || "N/A";

            const hrValues = segments
                .filter((s) => s.hr_mean !== undefined && !isNaN(s.hr_mean))
                .filter((s) => s.hr_min !== undefined && !isNaN(s.hr_min))
                .filter((s) => s.hr_max !== undefined && !isNaN(s.hr_max))
                .map((s) => ({
                    mean: s.hr_mean!,
                    min: s.hr_min!,
                    max: s.hr_max!,
                    start: s.segment_start_pt,
                }));

            let meanHR = "N/A", slowestHR = "N/A", fastestHR = "N/A";

            if (hrValues.length) {
                meanHR = `${Math.round(hrValues.reduce((sum, hr) => sum + hr.mean, 0) / hrValues.length)}`;
                const minHR = hrValues.filter(hr => !isNaN(hr.min));
                const maxHR = hrValues.filter(hr => !isNaN(hr.max));
                if (minHR.length) {
                    const slow = minHR.reduce((a, b) => (a.min < b.min ? a : b));
                    slowestHR = `${Math.round(slow.min)} (${slow.start?.split(' ')[1] || "Unknown"})`;
                }
                if (maxHR.length) {
                    const fast = maxHR.reduce((a, b) => (a.max > b.max ? a : b));
                    fastestHR = `${Math.round(fast.max)} (${fast.start?.split(' ')[1] || "Unknown"})`;
                }
            }

            return {
                rhythmName,
                totalSegments,
                burdenPercent,
                longestEpisodeMinutes,
                longestEpisodeStartTime,
                meanHR,
                slowestHR,
                fastestHR
            };
        })
        .filter(Boolean)
        .sort((a: any, b: any) => b.burdenPercent - a.burdenPercent);


    if (!data) {
        return <></>
    }

    return (
        <Row xs={1} md={1} className="g-2 mt-1">
            <Col>
                <Card>
                    <Card.Body id="waveform-container" className="waveform-availability" style={{ paddingBottom: '0px' }}>
                        <Row>
                            <Col md={6}>
                                <FontAwesomeIcon icon={faHeartPulse} /> <strong>Rhythms</strong>
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <div className="space-y-4">
                                    <div className="ecg-rhythm-legend text-small">{legendItems}</div>

                                    <div className="ecg-timeline-container relative h-8 bg-gray-100 rounded">{timelineSegments}</div>

                                    {selectedSegment && (
                                        <Row>
                                            <Col md={4}>
                                                <Card>
                                                    <Card.Body>
                                                        <strong>Selected Segment Details</strong>
                                                        <div className="grid grid-cols-2 gap-4 mt-2 text-sm">
                                                            <div>
                                                                <span className="font-medium">Rhythm: </span>{selectedSegment.segment_rhythm}
                                                            </div>
                                                            <div>
                                                                <span className="font-medium">Duration: </span>{selectedSegment.segment_duration}s
                                                            </div>
                                                            <div>
                                                                <span className="font-medium">Start: </span>{getLocalDateTimeStr(selectedSegment.segment_start_pt)}
                                                            </div>
                                                            <div>
                                                                <span className="font-medium">End: </span>{getLocalDateTimeStr(selectedSegment.segment_end_pt)}
                                                            </div>
                                                            {(selectedSegment.hr_mean || selectedSegment.hr_min || selectedSegment.hr_max) && (
                                                                <div>
                                                                    <span className="font-medium">HR (mean/range): </span>
                                                                    {(() => {
                                                                        const mean = selectedSegment.hr_mean ? Math.round(selectedSegment.hr_mean) : "N/A";
                                                                        const min = selectedSegment.hr_min ? Math.round(selectedSegment.hr_min) : "N/A";
                                                                        const max = selectedSegment.hr_max ? Math.round(selectedSegment.hr_max) : "N/A";
                                                                        return mean !== "N/A" && min !== "N/A" && max !== "N/A"
                                                                            ? `${mean} (${min}-${max})`
                                                                            : mean;
                                                                    })()}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </Card.Body>
                                                </Card>
                                            </Col>
                                        </Row>
                                    )}

                                    <div className="mt-4 mb-4">
                                        <strong>Rhythms Recorded:</strong>
                                        <Table className="text-small rhythm-table" hover>
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-4 py-2 text-left font-medium text-gray-600">Rhythm</th>
                                                    <th className="px-4 py-2 text-left font-medium text-gray-600">Burden (%)</th>
                                                    <th className="px-4 py-2 text-left font-medium text-gray-600">Episodes</th>
                                                    <th className="px-4 py-2 text-left font-medium text-gray-600">Mean HR</th>
                                                    <th className="px-4 py-2 text-left font-medium text-gray-600">Slowest HR</th>
                                                    <th className="px-4 py-2 text-left font-medium text-gray-600">Fastest HR</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {rhythmStats.map((stat, index) => {
                                                    if (!stat) {
                                                        return <></>
                                                    }
                                                    const isRowHighlighted = highlightedRhythm === stat.rhythmName;
                                                    return (
                                                        <tr
                                                            key={stat.rhythmName}
                                                            className={`cursor-pointer ${isRowHighlighted ? 'bg-blue-100 border-blue-300' : index % 2 === 0 ? 'bg-white hover:bg-gray-50' : 'bg-gray-50 hover:bg-gray-100'
                                                                }`}
                                                            onClick={() => setHighlightedRhythm(isRowHighlighted ? null : stat.rhythmName)}
                                                            title={`Click to highlight ${stat.rhythmName} segments on timeline`}
                                                        >
                                                            <td className="px-4 py-2 font-medium whitespace-nowrap">
                                                                <div className="flex items-center gap-2">
                                                                    <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: getRhythmColor(stat.rhythmName) }} />
                                                                    <span className="text-gray-900">{stat.rhythmName}</span>
                                                                </div>
                                                            </td>
                                                            <td className="px-4 py-2 whitespace-nowrap">
                                                                <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                                                                    {stat.burdenPercent.toFixed(1)}%
                                                                </span>
                                                            </td>
                                                            <td className="px-4 py-2 whitespace-nowrap">{stat.totalSegments}</td>
                                                            <td className="px-4 py-2 whitespace-nowrap">{stat.meanHR}</td>
                                                            <td className="px-4 py-2 whitespace-nowrap">{stat.slowestHR}</td>
                                                            <td className="px-4 py-2 whitespace-nowrap">{stat.fastestHR}</td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </Table>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>
            </Col>
        </Row>
    );
};

export default ECGRhythmTimeline;
